import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  ArrowRight,
  ArrowLeft,
  CreditCard,
  Loader2,
} from 'lucide-react';

const TOTAL_STEPS = 4;

const CustomerReferencePage = () => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    practiceName: '',
    providers: '',
    role: '',
    message: '',
  });

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSelectChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (step < TOTAL_STEPS) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.email ||
      !formData.phone ||
      !formData.practiceName ||
      !formData.providers ||
      !formData.role
    ) {
      setSubmitError('Please fill in all required fields.');
      return;
    }

    if (!agreedToTerms) {
      setSubmitError('You must accept the Customer Reference Call Agreement and NDA before submitting.');
      return;
    }

    setIsSubmitting(true);

    try {
      const isLocalhost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' ||
          window.location.hostname === '127.0.0.1');
      const apiUrl =
        process.env.REACT_APP_API_URL ||
        (isLocalhost ? 'http://localhost:8001' : '');

      const response = await fetch(`${apiUrl}/api/customer-reference`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, agreedToTerms }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.error || 'Failed to submit request');
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting customer reference request:', error);
      setSubmitError(
        error.message || 'There was an error submitting your request. Please try again.'
      );
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white selection:bg-blue-100 selection:text-blue-900">
      <Header />

      <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>

      <main className="pt-24 pb-12 px-4 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-50 via-white to-indigo-50/50"></div>
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
          <div className="absolute top-0 -left-4 w-[500px] h-[500px] bg-purple-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-[500px] h-[500px] bg-indigo-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-4000"></div>
        </div>

        <div className="container mx-auto max-w-2xl">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight text-center mb-6">
            Request a customer reference call
          </h1>
          <div className="w-full relative z-10">
            <Card className="shadow-2xl border-0 ring-1 ring-slate-900/5 bg-white/90 backdrop-blur-sm overflow-hidden">
              <div className="h-1.5 bg-slate-100 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-600 transition-all duration-300"
                  style={{ width: `${(isSubmitted ? 1 : step / TOTAL_STEPS) * 100}%` }}
                />
              </div>

              {isSubmitted ? (
                <CardContent className="px-6 py-10">
                  <div className="space-y-5 text-center">
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-blue-100 bg-blue-50">
                      <CreditCard className="h-7 w-7 text-blue-600" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-xl font-bold text-slate-900">
                        One last step — pay your deposit
                      </h3>
                      <p className="text-sm text-slate-500 leading-relaxed max-w-md mx-auto">
                        Your request will not be submitted until your <span className="font-semibold text-slate-900">$250 deposit</span> is paid. Please complete payment using the link below to confirm your reference call request.
                      </p>
                    </div>
                    <Button
                      type="button"
                      onClick={() =>
                        window.open(
                          'https://buy.stripe.com/3cI6oI5BzbW32Cm7To8AE13',
                          '_blank',
                          'noopener,noreferrer'
                        )
                      }
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11 text-base font-semibold rounded-lg shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all"
                    >
                      Pay $250 deposit
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                    <p className="text-[11px] text-slate-400 leading-relaxed max-w-sm mx-auto">
                      Payment is processed securely by Stripe.
                    </p>
                  </div>
                </CardContent>
              ) : (
                <>
                  <CardHeader className="pb-3 pt-6 px-6">
                    <CardTitle className="text-lg font-bold text-slate-900">
                      {step === 1 && 'About our customer reference call process'}
                      {step === 2 && 'Reference call format'}
                      {step === 3 && 'Reference call guidelines'}
                      {step === 4 && 'Your information'}
                    </CardTitle>
                    <CardDescription className="text-slate-500 text-sm mt-1">
                      {step === 1 && 'Please review the points below before continuing.'}
                      {step === 2 && "Here's how your reference call will work."}
                      {step === 3 && 'A few ground rules before you join the call.'}
                      {step === 4 && "Tell us a bit about you and we'll be in touch."}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="px-6 pb-6">
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (step < TOTAL_STEPS) handleNext();
                          else handleSubmit(e);
                        }}
                      >
                        {/* STEP 1: About our customer reference call process */}
                        {step === 1 && (
                          <ol className="list-decimal pl-6 space-y-3 text-sm text-slate-700 leading-relaxed marker:text-blue-600 marker:font-semibold">
                            <li>
                              <span className="font-semibold text-slate-900">Reference Call Fee.</span>{' '}
                              Due to an excessive number of requests to speak with customers, we require a <span className="font-semibold text-slate-900">$250 deposit</span> to schedule a reference call. If you decide to move forward and sign your contract within <span className="font-semibold text-slate-900">14 days</span> of having your reference call, we will credit the deposit towards your plan with us. In case you do not move forward, the $250 will not be refunded.
                            </li>
                            <li>
                              <span className="font-semibold text-slate-900">Best effort match.</span>{' '}
                              Due to the overwhelming demand for our AI OS, we receive an excessive number of requests for reference calls. While we will make our best effort to connect you with an office that has availability to speak to you, there is a chance that we may not be able to fulfill your request depending on the volume of requests we are managing at that time. If that is the case, we will refund your $250 deposit. If we are able to fulfill your request, we cannot guarantee you will be connected with an office that is using the same engagement model or the same PMS as you.
                            </li>
                          </ol>
                        )}

                        {/* STEP 2: Reference call format */}
                        {step === 2 && (
                          <ol className="list-decimal pl-6 space-y-3 text-sm text-slate-700 leading-relaxed marker:text-blue-600 marker:font-semibold">
                            <li>
                              Customer reference calls are <span className="font-semibold text-slate-900">20 minutes over Google Meet</span>. We arrange for the customer to be present along with one of our account managers on the call.
                            </li>
                            <li>
                              <span className="font-semibold text-slate-900">Best topics to discuss.</span>{' '}
                              Reference calls are best used to ask about the <span className="font-semibold">ROI</span> behind the solution, how the <span className="font-semibold">support and engagement</span> has been, and the overall experience of working with our team.
                            </li>
                          </ol>
                        )}

                        {/* STEP 3: Reference call guidelines */}
                        {step === 3 && (
                          <ol className="list-decimal pl-6 space-y-3 text-sm text-slate-700 leading-relaxed marker:text-blue-600 marker:font-semibold">
                            <li>
                              <span className="font-semibold text-slate-900">Professional conduct.</span>{' '}
                              Any unprofessional behavior with customers may result in legal action. Please treat our customers with empathy and respect, and refrain from any conduct that may not be appropriate in a workplace.
                            </li>
                            <li>
                              <span className="font-semibold text-slate-900">Please do not discuss pricing.</span>{' '}
                              During reference calls, we ask that you do not discuss pricing terms. Depending on which customer you are routed to, you may have pricing that is custom to your office (received as a quote) and their office's conditions may be very different. We ask that you refrain from discussing pricing — otherwise, you will incur an additional fee and be in violation of our agreement.
                            </li>
                            <li>
                              <span className="font-semibold text-slate-900">Keep it to arranged communication.</span>{' '}
                              Please do not reach out to the customer yourself outside of the call we arrange. We have received complaints before and cannot risk annoying our customers with unsolicited outreach.
                            </li>
                            <li>
                              <span className="font-semibold text-slate-900">Liability for contract loss.</span>{' '}
                              Please note that if unprofessional conduct, violation of these guidelines, or the manner of your engagement with a customer results in the loss of a contract, you may be liable to pay the equivalent of the <span className="font-semibold text-slate-900">3-year annual contract value</span>.
                            </li>
                          </ol>
                        )}

                        {/* STEP 4: Form */}
                        {step === 4 && (
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-1.5">
                                <Label htmlFor="firstName" className="text-xs font-semibold text-slate-700">First name *</Label>
                                <Input
                                  id="firstName"
                                  placeholder="Jane"
                                  value={formData.firstName}
                                  onChange={handleInputChange}
                                  required
                                  className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                                />
                              </div>
                              <div className="space-y-1.5">
                                <Label htmlFor="lastName" className="text-xs font-semibold text-slate-700">Last name *</Label>
                                <Input
                                  id="lastName"
                                  placeholder="Doe"
                                  value={formData.lastName}
                                  onChange={handleInputChange}
                                  required
                                  className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                                />
                              </div>
                            </div>

                            <div className="space-y-1.5">
                              <Label htmlFor="email" className="text-xs font-semibold text-slate-700">Work email *</Label>
                              <Input
                                id="email"
                                type="email"
                                placeholder="jane@practice.com"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                                className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                              />
                            </div>

                            <div className="space-y-1.5">
                              <Label htmlFor="phone" className="text-xs font-semibold text-slate-700">Phone number *</Label>
                              <Input
                                id="phone"
                                type="tel"
                                placeholder="(555) 000-0000"
                                value={formData.phone}
                                onChange={handleInputChange}
                                required
                                className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                              />
                            </div>

                            <div className="space-y-1.5">
                              <Label htmlFor="practiceName" className="text-xs font-semibold text-slate-700">Practice name *</Label>
                              <Input
                                id="practiceName"
                                placeholder="Bright Smile Dental"
                                value={formData.practiceName}
                                onChange={handleInputChange}
                                required
                                className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                              />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-1.5">
                                <Label htmlFor="providers" className="text-xs font-semibold text-slate-700">Providers *</Label>
                                <Select onValueChange={(value) => handleSelectChange('providers', value)} required>
                                  <SelectTrigger className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 h-9 text-sm">
                                    <SelectValue placeholder="Select..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="1-2">1-2</SelectItem>
                                    <SelectItem value="3-5">3-5</SelectItem>
                                    <SelectItem value="6-10">6-10</SelectItem>
                                    <SelectItem value="11+">11+</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-1.5">
                                <Label htmlFor="role" className="text-xs font-semibold text-slate-700">Role *</Label>
                                <Select onValueChange={(value) => handleSelectChange('role', value)} required>
                                  <SelectTrigger className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 h-9 text-sm">
                                    <SelectValue placeholder="Select..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="owner">Owner/Dentist</SelectItem>
                                    <SelectItem value="manager">Office Manager</SelectItem>
                                    <SelectItem value="front-desk">Front Desk</SelectItem>
                                    <SelectItem value="other">Other</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            <div className="space-y-1.5">
                              <Label htmlFor="message" className="text-xs font-semibold text-slate-700">
                                What would you like to learn on the call? <span className="text-slate-400 font-normal">(Optional)</span>
                              </Label>
                              <Textarea
                                id="message"
                                placeholder="e.g. ROI we've seen, what implementation looked like, ongoing support experience..."
                                value={formData.message}
                                onChange={handleInputChange}
                                className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 min-h-[80px] resize-none transition-all text-sm"
                              />
                            </div>

                            <div className="flex items-start gap-2.5 pt-2">
                              <input
                                type="checkbox"
                                id="agreedToTerms"
                                checked={agreedToTerms}
                                onChange={(e) => setAgreedToTerms(e.target.checked)}
                                required
                                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                              />
                              <label htmlFor="agreedToTerms" className="text-xs text-slate-600 leading-relaxed cursor-pointer">
                                I have read and agree to the{' '}
                                <Link to="/customer-reference-agreement" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline font-semibold">
                                  Customer Reference Call Agreement
                                </Link>{' '}
                                and the{' '}
                                <Link to="/nda" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline font-semibold">
                                  Non-Disclosure Agreement
                                </Link>
                                . I understand this is a binding agreement. *
                              </label>
                            </div>

                            {submitError && (
                              <p className="text-sm text-red-600 pt-1" role="alert">
                                {submitError}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Navigation buttons */}
                        <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleBack}
                            disabled={step === 1 || isSubmitting}
                            className="text-slate-600 border-slate-200 hover:bg-slate-50 h-10 px-5 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <ArrowLeft className="mr-2 w-4 h-4" />
                            Back
                          </Button>

                          <Button
                            type="submit"
                            disabled={isSubmitting || (step === TOTAL_STEPS && !agreedToTerms)}
                            className="bg-blue-600 hover:bg-blue-700 text-white h-10 px-6 text-sm font-semibold rounded-lg shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isSubmitting ? (
                              <>
                                <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                                Submitting...
                              </>
                            ) : step < TOTAL_STEPS ? (
                              <>
                                I understand
                                <ArrowRight className="ml-2 w-4 h-4" />
                              </>
                            ) : (
                              <>
                                Submit Request
                                <ArrowRight className="ml-2 w-4 h-4" />
                              </>
                            )}
                          </Button>
                        </div>

                        <p className="text-[10px] text-center text-slate-400 mt-4">
                          Step {step} of {TOTAL_STEPS} — A $250 deposit will be invoiced separately after submission.
                        </p>
                      </form>
                    </CardContent>
                  </>
                )}
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CustomerReferencePage;
